import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../lib/security';
import { Archive, BookOpen, Settings2, ArrowRight, Clock } from 'lucide-react';
import { useWikiDocs, WikiTOC, articlesUnder, findSectionPath } from '../utils/wikiToc';

/**
 * The documentation landing page: every top-level section as a card carrying a
 * blurb of what it covers. Choosing one opens the section, where the tree moves
 * to the left rail alongside the article.
 */
const WikiHomePage: React.FC = () => {
    const { canWrite, canAdmin } = useAuth();
    const { sections, articles, loading, error } = useWikiDocs();

    /** Sections without an authored summary fall back to naming what is inside. */
    const describeSection = (section: WikiTOC): string | null => {
        if (section.summary?.trim()) return section.summary.trim();

        const childNames = (section.children || []).map(c => c.description);
        if (childNames.length > 0) {
            return childNames.slice(0, 4).join(' · ') + (childNames.length > 4 ? ' …' : '');
        }
        return null;
    };

    const recentlyUpdated = [...articles]
        .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
        .slice(0, 6)
        .map(article => ({
            ...article,
            // The full section trail, so a title alone is not the only clue to
            // where the article lives.
            path: findSectionPath(sections, article.tocId)
                .map(section => section.description)
                .join(' / '),
        }));

    return (
        <div className="bx-wrap py-4 fade-in">
            <div>
                {/* Header */}
                <div className="flex items-start justify-between gap-4 mb-8">
                    <div className="max-w-2xl">
                        <h1 className="text-3xl font-bold text-gray-800">Documentation</h1>
                        <p className="text-gray-500 mt-2">
                            Guides, references and how-tos for the Binary platform. Pick an area
                            below to browse its articles.
                        </p>
                    </div>
                    {(canWrite('wiki') || canAdmin('wiki')) && (
                        <div className="shrink-0 flex items-center gap-2">
                            {canAdmin('wiki') && <Link
                                to="/wiki/contents"
                                className="inline-flex items-center gap-1.5 px-3 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium"
                                title="Manage contents"
                            >
                                <Settings2 className="w-4 h-4" />
                                Manage contents
                            </Link>}
                            {canAdmin('wiki') && <Link
                                to="/wiki/backups"
                                className="inline-flex items-center gap-1.5 px-3 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium"
                                title="Export the wiki as a zip archive"
                            >
                                <Archive className="w-4 h-4" />
                                Backups
                            </Link>}
                            {canWrite('wiki') && <Link
                                to="/wiki/edit"
                                className="px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 transition-colors text-sm font-medium"
                            >
                                + New Article
                            </Link>}
                        </div>
                    )}
                </div>

                {error && (
                    <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                        {error}
                    </div>
                )}

                {loading ? (
                    <div className="flex justify-center py-16">
                        <div className="spinner"></div>
                    </div>
                ) : sections.length === 0 ? (
                    <div className="bg-white rounded-xl border border-gray-200 py-16 text-center">
                        <BookOpen className="w-8 h-8 text-gray-300 mx-auto mb-3" />
                        <p className="text-gray-500">No documentation sections yet.</p>
                        {canAdmin('wiki') && (
                            <Link to="/wiki/contents" className="text-sm text-brand-600 hover:underline mt-2 inline-block">
                                Create the first one
                            </Link>
                        )}
                    </div>
                ) : (
                    <>
                        {/* Section cards */}
                        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5">
                            {sections.map(section => {
                                const count = articlesUnder(articles, section).length;
                                const blurb = describeSection(section);
                                const subCount = (section.children || []).length;

                                return (
                                    <Link
                                        key={section.id}
                                        to={`/wiki/section/${section.id}`}
                                        className="group flex flex-col bg-white rounded-xl border border-gray-200 p-5 hover:border-brand-300 hover:shadow-md transition-all"
                                    >
                                        <div className="flex items-start gap-3 mb-3">
                                            <span className="shrink-0 w-9 h-9 rounded-lg bg-brand-50 text-brand-600 flex items-center justify-center group-hover:bg-brand-100 transition-colors">
                                                <BookOpen className="w-4 h-4" />
                                            </span>
                                            <h2 className="text-lg font-semibold text-gray-900 group-hover:text-brand-700 transition-colors break-words leading-snug">
                                                {section.description}
                                            </h2>
                                        </div>

                                        <p className="text-sm text-gray-500 leading-relaxed line-clamp-3 flex-1">
                                            {blurb || 'Browse the articles in this section.'}
                                        </p>

                                        <div className="mt-4 pt-3 border-t border-gray-100 flex items-center justify-between text-xs text-gray-400">
                                            <span>
                                                {count} {count === 1 ? 'article' : 'articles'}
                                                {subCount > 0 && ` · ${subCount} ${subCount === 1 ? 'sub-section' : 'sub-sections'}`}
                                            </span>
                                            <ArrowRight className="w-4 h-4 text-gray-300 group-hover:text-brand-600 group-hover:translate-x-0.5 transition-all" />
                                        </div>
                                    </Link>
                                );
                            })}
                        </div>

                        {/* Recently updated */}
                        {recentlyUpdated.length > 0 && (
                            <div className="mt-10">
                                <h2 className="flex items-center gap-2 text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">
                                    <Clock className="w-4 h-4" />
                                    Recently updated
                                </h2>
                                <div className="bg-white rounded-xl border border-gray-200 divide-y divide-gray-100">
                                    {recentlyUpdated.map(article => (
                                        <Link
                                            key={article.id}
                                            to={`/wiki/article/${article.id}`}
                                            className="flex items-center justify-between gap-3 px-5 py-3 hover:bg-gray-50 transition-colors group"
                                        >
                                            <span className="flex flex-wrap items-baseline gap-x-2 min-w-0">
                                                <span className="text-sm text-gray-800 group-hover:text-brand-700 break-words">
                                                    {article.title}
                                                </span>
                                                {article.path && (
                                                    <span className="text-xs text-gray-400 break-words">
                                                        {article.path}
                                                    </span>
                                                )}
                                            </span>
                                            <span className="shrink-0 text-xs text-gray-400">
                                                {new Date(article.updatedAt).toLocaleDateString()}
                                                {article.updatedByName && ` · ${article.updatedByName}`}
                                            </span>
                                        </Link>
                                    ))}
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default WikiHomePage;
